import * as React from "react";
import {
  Link,
  Outlet,
  createRootRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Book,
  Brain,
  CaretLeft,
  CaretRight,
  GearSix,
  GitBranch,
  GitPullRequest,
  Kanban,
  List,
  Robot,
  NotePencil,
  Stack,
  X,
} from "@phosphor-icons/react";
import { useMutation } from "convex/react";
import { api } from "@alfred/convex/_generated/api";
import { Button } from "~/components/ui/button";
import { Wordmark } from "~/components/Wordmark";
import { SidebarNav } from "~/components/SidebarNav";
import { SidebarItem } from "~/components/SidebarItem";
import { cn } from "~/lib/utils";

export const Route = createRootRoute({
  component: RootComponent,
});

const APP_LINKS = [
  { to: "/", label: "Board", icon: Kanban },
  { to: "/tickets", label: "Tickets", icon: Stack },
  { to: "/tickets/new", label: "New Ticket", icon: NotePencil },
  { to: "/repos", label: "Repos", icon: GitBranch },
  { to: "/agents", label: "Agent Runs", icon: Robot },
  { to: "/pr-reviews", label: "PR Reviews", icon: GitPullRequest },
  { to: "/memory", label: "Memory", icon: Brain },
  { to: "/brief", label: "Tech Brief", icon: Book },
  { to: "/settings", label: "Settings", icon: GearSix },
] as const;

function RootComponent() {
  return <AppShell />;
}

function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigate();
  const triggerSync = useMutation(api.tickets.requestJiraSync);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const [collapsed, setCollapsed] = React.useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.hasAttribute("data-sidebar-collapsed"),
  );
  const [isLg, setIsLg] = React.useState(false);
  React.useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    setIsLg(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsLg(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isRailMode = collapsed && isLg;

  function toggleCollapsed() {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem("alfred:sidebar-collapsed", next ? "1" : "0");
        if (next) {
          document.documentElement.setAttribute(
            "data-sidebar-collapsed",
            "",
          );
        } else {
          document.documentElement.removeAttribute("data-sidebar-collapsed");
        }
      } catch {}
      return next;
    });
  }

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <div className="relative flex h-full flex-col bg-background text-foreground">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border/70 bg-background/85 px-4 py-3 backdrop-blur lg:hidden">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle sidebar"
          className="rounded-xl"
        >
          {sidebarOpen ? <X size={16} /> : <List size={16} />}
        </Button>
        <Wordmark className="text-sm font-semibold tracking-wide" />
        <Button
          type="button"
          size="icon"
          onClick={() => nav({ to: "/" })}
          aria-label="Board"
          className="rounded-xl"
        >
          <Kanban size={16} />
        </Button>
      </header>

      <div className="flex min-h-0 flex-1">
        <div
          className={cn(
            "fixed inset-0 z-20 bg-foreground/40 transition-opacity duration-[var(--dur-smooth)] ease-[var(--ease-in-out-quart)] lg:hidden",
            sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
        <aside
          style={
            { "--sidebar-w": collapsed ? "4rem" : "17rem" } as React.CSSProperties
          }
          className={cn(
            "fixed inset-y-0 left-0 z-30 flex flex-col border-r border-sidebar-border bg-sidebar sidebar-surface p-3 text-sidebar-foreground",
            "transition-[transform,width] duration-[var(--dur-smooth)] ease-[var(--ease-in-out-quart)]",
            "lg:static lg:z-0 lg:translate-x-0 lg:w-[var(--sidebar-w)]",
            "w-[min(85vw,17rem)]",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
          )}
          aria-label="Primary navigation"
        >
          <div
            className={cn(
              "mb-3 flex items-center px-1 py-1",
              isRailMode ? "justify-center" : "justify-between gap-2",
            )}
          >
            <div
              className={cn(
                "min-w-0 overflow-hidden transition-[opacity,max-width] duration-[var(--dur-smooth)] ease-[var(--ease-in-out-quart)]",
                isRailMode ? "max-w-0 opacity-0" : "max-w-[12rem] opacity-100",
              )}
            >
              <Wordmark />
              <p className="text-xs text-sidebar-foreground/50">
                Full-Stack Engineer Agent
              </p>
            </div>
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              className="hidden shrink-0 lg:flex size-8 items-center justify-center rounded-lg text-sidebar-foreground/50 hover:bg-white/5 hover:text-sidebar-foreground transition active:scale-[0.92]"
            >
              {collapsed ? (
                <CaretRight size={16} aria-hidden />
              ) : (
                <CaretLeft size={16} aria-hidden />
              )}
            </button>
          </div>

          <Button
            type="button"
            onClick={() => triggerSync({}).catch(() => undefined)}
            aria-label={isRailMode ? "Sync Jira" : undefined}
            className={cn(
              "mb-3 rounded-xl",
              isRailMode && "aspect-square p-0",
            )}
          >
            <Robot size={16} aria-hidden />
            {!isRailMode && "Sync Jira now"}
          </Button>

          <SidebarNav collapsed={isRailMode} className="mb-3 space-y-1">
            {APP_LINKS.map((item) => (
              <SidebarItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                active={
                  item.to === "/"
                    ? pathname === "/"
                    : pathname.startsWith(item.to)
                }
              />
            ))}
          </SidebarNav>

          <div className="mt-auto pt-3">
            <div
              className={cn(
                "rounded-2xl border border-sidebar-border bg-white/5",
                isRailMode ? "flex items-center justify-center p-2" : "px-3 py-2",
              )}
            >
              {isRailMode ? (
                <div className="flex size-8 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
                  A
                </div>
              ) : (
                <>
                  <p className="line-clamp-1 text-sm font-medium text-sidebar-foreground">
                    Alfred
                  </p>
                  <p className="text-xs text-sidebar-foreground/50">
                    Local Cursor runtime
                  </p>
                </>
              )}
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            key={pathname}
            className="anim-enter-fade min-h-0 min-w-0 flex-1 overflow-y-auto"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
